/**
 * Shared "Issue closing" section for every phased worker/analyst
 * bundle (codedrifters/packages#921).
 *
 * Every phased bundle's agent loop already defers the close to the
 * consumer — *"Commit, push, open a PR (if applicable), and close the
 * issue per your project's PR workflow."* The per-phase steps used to
 * drop that qualifier and say "Close the research issue." flat, which
 * contradicts the loop: in any consumer whose PR body carries a closing
 * keyword (`Closes #<n>`), the phase-end close fires **before** the PR
 * merges, so the keyword no-ops and the issue leaves the `status:*`
 * state machine early.
 *
 * The rule is rendered identically into every phased prompt via
 * {@link renderIssueClosePolicySection}, and each per-phase step defers
 * to it with {@link ISSUE_CLOSE_RULE_REFERENCE}, so the guidance cannot
 * drift per-bundle again — the same pattern the claim-verification,
 * project-context, and progress-file sections use.
 */

/**
 * Heading of the shared section, without the markdown `##` prefix.
 * Exported so the per-step reference and any test assert against one
 * string.
 */
export const ISSUE_CLOSE_SECTION_TITLE = "Issue closing";

/**
 * Clause a per-phase step appends when it tells the agent to close its
 * own issue. Points at the shared section instead of restating it.
 *
 * @example
 * `7. **Commit and push** the notes file. Close the research issue ${ISSUE_CLOSE_RULE_REFERENCE}.`
 */
export const ISSUE_CLOSE_RULE_REFERENCE = `per the **${ISSUE_CLOSE_SECTION_TITLE}** rule`;

/**
 * Render the shared `## Issue closing` section as prompt lines. Spread
 * into a sub-agent `prompt` array with
 * `...renderIssueClosePolicySection()`.
 */
export function renderIssueClosePolicySection(): ReadonlyArray<string> {
  return [
    `## ${ISSUE_CLOSE_SECTION_TITLE}`,
    "",
    "**Never close a phase issue that your PR is going to close for**",
    "**you.** Most projects put a closing keyword (`Closes #<n>`) in the",
    "PR body, so the issue closes when the PR merges — and that merge is",
    "what drives the documented `status:ready-for-review` →",
    "`status:done` transition.",
    "",
    "Closing the issue yourself at the end of a phase pre-empts all of",
    "that:",
    "",
    "- the PR's `Closes #<n>` line becomes a no-op — nothing happens at",
    "  merge time;",
    "- the issue closes while still carrying `status:in-progress` or",
    "  `status:ready-for-review`, so its labels describe a state it has",
    "  already left;",
    "- any caller that applies a status label afterwards is labelling a",
    "  closed issue, and a caller that verifies workflow state has to",
    "  reopen the issue to put it back in the pipeline.",
    "",
    "So whenever a phase step tells you to close its issue, resolve it",
    "against your project's PR workflow first:",
    "",
    "1. **The PR closes it.** Your PR body carries `Closes #<n>` for this",
    "   issue — commit, push, open the PR, and leave the issue **open**.",
    "   Post the phase outcome the step asks for as an ordinary comment",
    "   on the issue, not as a closing comment, and let the merge perform",
    "   the close.",
    "2. **The PR does not close it.** There is no closing keyword for",
    "   this issue, or the phase lands no PR at all — close the issue",
    "   yourself, with the outcome comment the step describes.",
    "",
    "### Always close directly",
    "",
    "Two cases never wait on a merge:",
    "",
    "- **Aborts.** A phase that stops without a deliverable — nothing",
    "  relevant found, a duplicate, an ambiguity handed off with",
    "  `status:needs-attention` — closes its issue directly. No PR will",
    "  ever reference it.",
    "- **Someone else's issue.** Closing an issue other than the one you",
    "  are working (a parent issue your phase resolves, a superseded",
    "  sibling) is bookkeeping, not the workflow close. A closing keyword",
    "  only ever covers the issues your PR body names.",
  ];
}
